import { useState, useEffect, ComponentType } from 'react';
import { useForm, UseFormReturn, FieldValues } from 'react-hook-form';
import { useAppRouter as useRouter } from '@/lib/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { CamundaFormsService, type StartFormInfo, type StartFormVariables } from '@/lib/camunda/forms-service';
import { CamundaProcessService } from '@/lib/camunda/process-service';
import type { StartProcessResult } from '@/lib/camunda';
import { BaseFormSchema } from '@/types/zod-schema';
import {
  getAvailableForms,
  loadStartFormComponent,
  loadStartSuccessComponent,
  loadFormPreloadData,
  getDefaultValuesFromSchema,
  type StartFormProps,
  type StartSuccessComponentProps
} from '@/lib/form-utilities';
import { resolveFormKey } from '@/lib/process-registry';
import { throwError } from '@/lib/errors';

export interface DynamicStartFormProps extends StartFormProps {
  variables: StartFormVariables;
}

type SuccessComponentProps = StartSuccessComponentProps;

export interface DynamicStartFormResult {
  formInfo: StartFormInfo | null;
  FormComponent: ComponentType<DynamicStartFormProps> | null;
  SuccessComponent: ComponentType<SuccessComponentProps> | null;
  submissionState: 'form' | 'success';
  formKey: string | null;
  loading: boolean;
  error: string | null;
  form: UseFormReturn<FieldValues>;
  handleSubmit: () => Promise<void>;
  isSubmitting: boolean;
  availableForms: string[];
  submittedData: FieldValues | null;
  processInstanceId: string | null;
  processDefinitionName: string | null;
  preloadedData: FieldValues | null;
  preloadPending: boolean;
}

export function useDynamicStartForm(processDefinitionId: string, specificFormKey?: string): DynamicStartFormResult {
  const router = useRouter();
  
  const [formInfo, setFormInfo] = useState<StartFormInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [FormComponent, setFormComponent] = useState<ComponentType<DynamicStartFormProps> | null>(null);
  const [availableForms, setAvailableForms] = useState<string[]>([]);
  const [schema, setSchema] = useState<BaseFormSchema | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionState, setSubmissionState] = useState<'form' | 'success'>('form');
  const [SuccessComponent, setSuccessComponent] = useState<ComponentType<SuccessComponentProps> | null>(null);
  const [submittedData, setSubmittedData] = useState<FieldValues | null>(null);
  const [processInstanceId, setProcessInstanceId] = useState<string | null>(null);
  const [processDefinitionName, setProcessDefinitionName] = useState<string | null>(null);
  const [preloadedData, setPreloadedData] = useState<FieldValues | null>(null);
  const [preloadPending, setPreloadPending] = useState(true);

  const form = useForm({
    mode: 'onChange',
    resolver: schema ? zodResolver(schema) : undefined,
    criteriaMode: 'all',
  });
  const { reset: resetForm } = form;

  const onSubmit = async (data: FieldValues) => {
    setIsSubmitting(true);
    try {
      const resolvedFormKey = formInfo?.key || specificFormKey || undefined;
      const result: StartProcessResult | null = await CamundaFormsService.submitStartForm(
        processDefinitionId,
        data,
        undefined,
        resolvedFormKey,
      );
      
      if (result && result.id) {
        setProcessInstanceId(result.id);
        
        if (SuccessComponent) {
          setSubmittedData(data);
          setSubmissionState('success');
        } else {
          router.push('/task-list/start-process');
        }
      } else {
        throwError(new Error('Error al iniciar el proceso'));
      }
    } catch (err) {
      console.error('Error submitting start form:', err);
      setError('Error al iniciar el proceso. Por favor, inténtelo de nuevo.');
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    let cancelled = false;
    let preloadStarted = false;
    let formLoadFailed = false;

    const loadFormAndSchema = async () => {
      setLoading(true);
      setError(null);
      setFormComponent(null);
      setSchema(null);
      setPreloadedData(null);
      setPreloadPending(true);
      resetForm({});

      try {
        const processDefinitionPromise = CamundaProcessService.getProcessDefinition(processDefinitionId);

        const forms = getAvailableForms();
        setAvailableForms(forms);
        
        let rawFormKeyToUse: string;
        let loadedFormInfo: StartFormInfo | null = null;
        let processDefinitionNameToUse: string | null = null;
        if (specificFormKey) {
          rawFormKeyToUse = specificFormKey;
        } else {
          const [processDefinition, startForm] = await Promise.all([
            processDefinitionPromise,
            CamundaFormsService.getStartForm(processDefinitionId)
          ]);

          if (processDefinition) {
            processDefinitionNameToUse = processDefinition.name || processDefinition.key || 'Proceso sin nombre';
          }
          if (!startForm?.key) {
            throwError(new Error('No se encontró formulario de inicio para este proceso'));
          }
          loadedFormInfo = startForm;
          rawFormKeyToUse = startForm.key;
        }
        
        const resolvedFormKey = resolveFormKey(rawFormKeyToUse, forms);
        if (!resolvedFormKey) {
          throwError(new Error(`Formulario '${rawFormKeyToUse}' no encontrado`));
        }

        if (cancelled) {
          return;
        }

        if (!specificFormKey) {
          setFormInfo(loadedFormInfo ? { ...loadedFormInfo, key: resolvedFormKey } : { key: resolvedFormKey });
        }

        const preloadPromise = loadFormPreloadData(resolvedFormKey);
        preloadStarted = true;
        const preloadOutcomePromise = preloadPromise.then(
          (preloadResult) => {
            if (!cancelled) {
              setPreloadPending(false);
            }
            return { status: 'fulfilled', preloadResult } as const;
          },
          (preloadError) => {
            const normalizedPreloadError = preloadError instanceof Error
              ? preloadError
              : new Error('Error al cargar los datos iniciales');
            if (!cancelled) {
              setPreloadPending(false);
            }
            return { status: 'rejected', preloadError: normalizedPreloadError } as const;
          },
        );
        const [
          processDefinition,
          loadedComponent,
          loadedSchema,
          loadedSuccessComponent
        ] = await Promise.all([
          specificFormKey ? processDefinitionPromise : Promise.resolve(null),
          loadStartFormComponent(resolvedFormKey),
          CamundaFormsService.loadFormSchema(resolvedFormKey, forms),
          loadStartSuccessComponent(resolvedFormKey)
        ]);

        if (cancelled) {
          return;
        }

        if (specificFormKey && processDefinition) {
          processDefinitionNameToUse = processDefinition.name || processDefinition.key || 'Proceso sin nombre';
        }

        if (!loadedComponent) {
          throwError(new Error(`Formulario '${resolvedFormKey}' no encontrado`));
        }
        if (!loadedSchema) {
          throwError(new Error(`Schema no encontrado para el formulario '${resolvedFormKey}'`));
        }
        
        const defaultValues = getDefaultValuesFromSchema(loadedSchema);
        setSchema(loadedSchema);
        if (processDefinitionNameToUse) {
          setProcessDefinitionName(processDefinitionNameToUse);
        }
        resetForm(defaultValues);
        setFormComponent(() => loadedComponent);
        setSuccessComponent(() => loadedSuccessComponent);

        void preloadOutcomePromise.then((preloadOutcome) => {
          if (cancelled) {
            return;
          }

          if (preloadOutcome.status === 'rejected') {
            console.error('Error loading start form preload:', preloadOutcome.preloadError);
            setError(preloadOutcome.preloadError.message);
            return;
          }

          const mergedValues = preloadOutcome.preloadResult.formValues
            ? { ...defaultValues, ...preloadOutcome.preloadResult.formValues }
            : defaultValues;

          resetForm(mergedValues, { keepDirtyValues: true });
          setPreloadedData(preloadOutcome.preloadResult.data);
        });

      } catch (err: unknown) {
        formLoadFailed = true;
        if (cancelled) {
          return;
        }
        console.error('Error loading start form:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar el formulario de inicio');
      }
    };
    
    void loadFormAndSchema().finally(() => {
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (!preloadStarted || formLoadFailed) {
        setPreloadPending(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [processDefinitionId, specificFormKey, resetForm]);

  const onError = (errors: FieldValues) => {
    console.log(`❌ Start form validation errors for process: ${processDefinitionId}`, errors);
  };

  return { 
    formInfo, 
    FormComponent,
    SuccessComponent,
    submissionState,
    formKey: formInfo?.key || specificFormKey || null,
    loading,
    error,
    form,
    handleSubmit: form.handleSubmit(onSubmit, onError),
    isSubmitting,
    availableForms,
    submittedData,
    processInstanceId,
    processDefinitionName,
    preloadedData,
    preloadPending,
  };
}
